import { AgreementsAction } from './agreements.action';
import { UtilitiesService } from '../../services/utilities.service';
import { InitializerAction } from '../initializer/initiliazer.action';

export type AgreementLoadingState = 'idle' | 'loading' | 'success' | 'error';

export interface AgreementLoadingStates {
    [contractGuid: string]: AgreementLoadingState;
}

export class AgreementsState {
    public agreements: any[];
    public agreementsToAccept: any[];
    public pending: boolean;
    public pendingDataRequests: number;
    public pendingAgreements: any[];
    public pendingAccept: number;
    public selectedAgreement: null;
    public error: null;
    public allAcceptedAgreements: any[];
    public acceptedAgreements: any;
    public acceptedAgreement: any;
    public agreementLoadingStates: AgreementLoadingStates;
}

export const initialGenericState: AgreementsState = {
    agreements: [],
    agreementsToAccept: [],
    pending: false,
    pendingDataRequests: 0,
    error: null,
    pendingAgreements: [],
    pendingAccept: 0,
    selectedAgreement: null,
    allAcceptedAgreements: [],
    acceptedAgreements: null,
    acceptedAgreement: [],
    agreementLoadingStates: {},
};

export function AgreementsReducer(state: AgreementsState = initialGenericState, action: any) {

    switch (action.type) {
        // accept
        case AgreementsAction.acceptAgreement:
            return { ...{}, ...state, ...{ pendingAccept: +state.pendingAccept + 1, error: null } };

        case AgreementsAction.acceptAgreementSuccess:
            return {
              ...state,
              ...{
                  pendingAccept: state.pendingAccept - 1,
                  error: null,
                  // Adiciona o novo contrato ao array
                  acceptedAgreement: [
                      ...state.acceptedAgreement,
                      action.payload
                  ],
              },
            };

        case AgreementsAction.acceptAgreementError:
            return { ...{}, ...state, ...{ pendingAccept: +state.pendingAccept - 1, error: action.payload } };

        // get item
        case AgreementsAction.getAgreement:
            return { ...{}, ...state, ...{ pending: true, error: null } };

        case AgreementsAction.getAgreementSuccess:
            return { ...{}, ...state, ...{ selectedAgreement: action.payload, error: null, pending: false } };

        case AgreementsAction.getAgreementError:
            return { ...{}, ...state, ...{ error: action.payload, pending: false } };

        // get item data
        case AgreementsAction.getAgreementData:
            return { ...{}, ...state, ...{ pendingDataRequests: state.pendingDataRequests + 1, error: null } };

        case AgreementsAction.getAgreementDataSuccess:
            return {
                ...{},
                ...state,
                ...{
                    agreements: UtilitiesService.mergeIndexableObject(
                        state.agreements,
                        [action.payload],
                        'contractGuid',
                    ),
                    error: null,
                    pendingDataRequests: state.pendingDataRequests - 1,
                },
            };

        case AgreementsAction.getAgreementDataError:
            return { ...{}, ...state, ...{ error: action.payload, pendingDataRequests: state.pendingDataRequests - 1 } };

        // get accepted agreements
        case AgreementsAction.getAccepted:
            return { ...state, ...{ pending: true, error: null } };

        case AgreementsAction.getAcceptedError:
            return { ...state, ...{ pending: false, error: action.payload } };

        case AgreementsAction.getAcceptedSuccess:
            return { ...state, ...{ acceptedAgreements: action.payload, error: null, pending: false } };

        case AgreementsAction.getMultipleAgreementsData: {
            const contractGuidsToLoad = action.payload as string[];
            const loadingStatesForMultiple: AgreementLoadingStates = {};
            contractGuidsToLoad.forEach((guid: string) => {
                loadingStatesForMultiple[guid.toLowerCase()] = 'loading';
            });
            return {
                ...state,
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    ...loadingStatesForMultiple,
                },
            };
        }

        case AgreementsAction.getMultipleAgreementsDataSuccess: {
            const successPayload = action.payload as any[];
            const successStates: AgreementLoadingStates = {};
            successPayload.forEach((agreement: any) => {
                if (agreement.contractGuid) {
                    successStates[agreement.contractGuid.toLowerCase()] = 'success';
                }
            });
            return {
                ...state,
                agreements: UtilitiesService.mergeIndexableObject(
                    state.agreements,
                    [...action.payload],
                    'contractGuid',
                ),
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    ...successStates,
                },
            };
        }

        case AgreementsAction.getMultipleAgreementsDataError: {
            const errorPayload = action.payload;
            const errorStates: AgreementLoadingStates = {};
            if (Array.isArray(errorPayload)) {
                errorPayload.forEach((failed: any) => {
                    if (failed.contractGuid) {
                        errorStates[failed.contractGuid.toLowerCase()] = 'error';
                    }
                });
            }
            return {
                ...state,
                error: action.payload,
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    ...errorStates,
                },
            };
        }

        case AgreementsAction.setAgreementToAccept:
            return { ...state, ...{ agreementsToAccept: action.payload } };

        // get agreement status
        case AgreementsAction.getAgreementStatus:
            return { ...state, ...{ pending: true, error: null } };

        case AgreementsAction.getAgreementStatusError:
            return { ...state, ...{ pending: false, error: action.payload } };

        case AgreementsAction.getAgreementStatusSuccess:
            return { ...state, ...{ acceptedAgreements: action.payload, error: null, pending: false } };

        case InitializerAction.resetUserRelatedData:
            return {
                ...state, ...{
                    agreementsToAccept: initialGenericState.agreementsToAccept,
                    allAcceptedAgreements: initialGenericState.allAcceptedAgreements,
                    acceptedAgreements: initialGenericState.acceptedAgreements,
                    acceptedAgreement: initialGenericState.acceptedAgreement,
                },
            };

        case AgreementsAction.resetSelectedAgreement:
            return { ...state, ...{ selectedAgreement: null, error: null, pending: false } };

        case AgreementsAction.retrySingleAgreementData: {
            const retryGuid = (action.payload as string).toLowerCase();
            return {
                ...state,
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    [retryGuid]: 'loading',
                },
            };
        }

        case AgreementsAction.retrySingleAgreementDataSuccess: {
            const retrySuccessAgreement = action.payload;
            const retrySuccessGuid = retrySuccessAgreement.contractGuid?.toLowerCase();
            return {
                ...state,
                agreements: UtilitiesService.mergeIndexableObject(
                    state.agreements,
                    [retrySuccessAgreement],
                    'contractGuid',
                ),
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    [retrySuccessGuid]: 'success',
                },
                error: null,
            };
        }

        case AgreementsAction.retrySingleAgreementDataError: {
            const retryErrorGuid = (action.payload?.contractGuid || action.payload)?.toLowerCase();
            return {
                ...state,
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    [retryErrorGuid]: 'error',
                },
                error: action.payload,
            };
        }

        case AgreementsAction.setAgreementLoadingState: {
            const { contractGuid: setGuid, loadingState } = action.payload;
            return {
                ...state,
                agreementLoadingStates: {
                    ...state.agreementLoadingStates,
                    [setGuid.toLowerCase()]: loadingState,
                },
            };
        }

        default:
            return state;
    }

}
